import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { useWorkspaceManager } from '../hooks/useWorkspaceManager';
import { WorkspaceWithRole } from '../services/workspace.service';

// Re-export WorkspaceWithRole for convenience
export type { WorkspaceWithRole };

export interface UserData {
  uid: string;
  email: string | null;
  display_name: string | null;
  photo_url: string | null;
  provider: string;
  is_anonymous: boolean;
  created_at: string;
  last_login: string;
}

export interface BetaBlockInfo {
  detail: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserData | null;
  idToken: string | null;
  loading: boolean;
  // Workspace management
  availableWorkspaces: WorkspaceWithRole[];
  currentWorkspace: WorkspaceWithRole | null;
  workspaceLoading: boolean;
  refreshWorkspaces: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  // Beta allowlist
  isBetaBlocked: boolean;
  betaBlockInfo: BetaBlockInfo | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  user: User | null;
  userProfile: UserData | null;
  idToken: string | null;
  loading: boolean;
  children: ReactNode;
}

export const AuthProvider = ({
  user,
  userProfile,
  idToken,
  loading,
  children,
}: AuthProviderProps) => {
  const {
    availableWorkspaces,
    currentWorkspace,
    loading: workspaceLoading,
    refreshWorkspaces,
    switchWorkspace,
  } = useWorkspaceManager({ user, userProfile });

  const [betaBlockInfo, setBetaBlockInfo] = useState<BetaBlockInfo | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<BetaBlockInfo>;
      if (ce.detail) {
        setBetaBlockInfo(ce.detail);
      }
    };
    window.addEventListener('beta:blocked', handler);
    return () => window.removeEventListener('beta:blocked', handler);
  }, []);

  const value: AuthContextType = {
    user,
    userProfile,
    idToken,
    loading,
    availableWorkspaces,
    currentWorkspace,
    workspaceLoading,
    refreshWorkspaces,
    switchWorkspace,
    isBetaBlocked: betaBlockInfo !== null,
    betaBlockInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
